import {NgModule} from '@angular/core'
import {BrowserModule} from '@angular/platform-browser'

import {AppRoutingModule} from './app-routing.module'
import {AppComponent} from './app.component'
import {ToastrModule} from 'ngx-toastr'
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http'
import {StoreModule} from '@ngrx/store'
import {appReducer} from 'src/store/reducer/app.reducer'
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService
} from '@azure/msal-angular'
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser'
import {environment} from 'environments/environment'
import {LoginComponent} from 'src/components/auth/login/login.component'
import {MatButtonModule} from '@angular/material/button'
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async'
import {AuthInterceptor} from 'src/services/interceptor/auth.interceptor'
import {HeaderComponent} from '../components/common/header/header.component'
import {DashboardComponent} from 'src/components/dashboard/dashboard.component'
import {MatTableModule} from '@angular/material/table'
import {MatRadioModule} from '@angular/material/radio'
import {MatTabsModule} from '@angular/material/tabs'
import {MatSelectModule} from '@angular/material/select'
import {FormsModule, ReactiveFormsModule} from '@angular/forms'
import {EffectsModule} from '@ngrx/effects'
import {AuthEffects} from 'src/store/effect/auth.effect'
import {TableComponent} from 'src/components/common/table/table.component'
import {MatPaginator} from '@angular/material/paginator'
import {MatFormField, MatFormFieldModule} from '@angular/material/form-field'
import {MatSort, MatSortHeader} from '@angular/material/sort'
import {MatDialogModule} from '@angular/material/dialog'
import {MatInputModule} from '@angular/material/input'
import {CommonModule, DatePipe} from '@angular/common'
import {MatDatepickerModule} from '@angular/material/datepicker'
import {MatCardModule} from '@angular/material/card'
import {MatNativeDateModule, provideNativeDateAdapter} from '@angular/material/core'
import {MatIconModule} from '@angular/material/icon'
import {ExpenseEffects} from 'src/store/effect/expense.effect'
import {BrowserAnimationsModule} from '@angular/platform-browser/animations'
import {CommonEffects} from 'src/store/effect/common.effect'
import {MatGridList, MatGridTile} from '@angular/material/grid-list'
import {MatSlideToggleModule} from '@angular/material/slide-toggle'
import {PaymentEffects} from 'src/store/effect/payment.effect'
import {MatMenuModule} from '@angular/material/menu'
import {AutomationProcessRegisterEffects} from 'src/store/effect/automation-process-register.effect'
import {EmployeeEffects} from 'src/store/effect/employee.effect'
import {BankingInfoComponent} from 'src/components/banking-info/banking-info.component'
import {MatListModule} from '@angular/material/list'
import {CommentDialogComponent} from 'src/components/common/comment-dialog/comment-dialog.component'
import {DashboardEffects} from 'src/store/effect/dashboard.effect'
import {ExpenseReportComponent} from 'src/components/expense-report/expense-report.component'
import {ExecutionLogsComponent} from 'src/components/execution-logs/execution-logs.component'
import {PaymentHistoryDialogComponent} from 'src/components/common/payment-history-dialog/payment-history-dialog.component'
import {TableCellComponent} from 'src/components/common/table/table-cell/table-cell.component'
import {TableHeaderCellComponent} from 'src/components/common/table/table-header-cell/table-header-cell.component'
import {AdhocEffect} from "../store/effect/adhoc.effect";
import {SortByStatusOrderPipe} from "../pipes/status-group-sorting.pipe";
import {ExpensesComponent} from "../components/expenses/expenses.component";
import {StatusComponent} from "../components/common/status/status.component";
import {MatChip, MatChipGrid, MatChipRow, MatChipSet} from "@angular/material/chips";
import {EmployeeDetailsDialogComponent} from "../components/common/employee-details-dialog/employee-details-dialog.component";
import {ExpenseDetailsDialogComponent} from "../components/common/expense-details-dialog/expense-details-dialog.component";
import {FilterPanelComponent} from "../components/common/filter-panel/filter-panel.component";
import {FileRepositoryDialogComponent} from "../components/common/file-repository-dialog/file-repository-dialog.component";
import {
  ExecutionSummaryDialogComponent
} from "../components/common/execution-summary-dialog/execution-summary-dialog.component";
import {MatAccordion, MatExpansionPanel, MatExpansionPanelTitle, MatExpansionPanelHeader} from "@angular/material/expansion";

export function loggerCallback(logLevel: LogLevel, message: string) {}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  })
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>()
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes)

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  }
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes]
    },
    loginFailedRoute: '/login-failed'
  }
}

@NgModule({
  declarations: [
    AppComponent,
    TableComponent,
    ExecutionLogsComponent,
    LoginComponent,
    DashboardComponent,
    BankingInfoComponent,
    CommentDialogComponent,
    ExpenseReportComponent,
    PaymentHistoryDialogComponent,
    TableHeaderCellComponent,
    TableCellComponent,
    SortByStatusOrderPipe,
    ExpensesComponent,
    StatusComponent,
    EmployeeDetailsDialogComponent,
    ExpenseDetailsDialogComponent,
    FilterPanelComponent,
    FileRepositoryDialogComponent,
    ExecutionSummaryDialogComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ToastrModule.forRoot({
      timeOut: 10000,
      positionClass: 'toast-top-right',
      preventDuplicates: true
    }),
    StoreModule.forRoot(appReducer),
    MsalModule,
    MatButtonModule,
    HeaderComponent,
    MatTableModule,
    MatPaginator,
    MatFormField,
    MatSort,
    MatSortHeader,
    EffectsModule.forRoot([
      AuthEffects,
      ExpenseEffects,
      CommonEffects,
      PaymentEffects,
      AutomationProcessRegisterEffects,
      EmployeeEffects,
      DashboardEffects,
      AdhocEffect
    ]),
    MatTableModule,
    MatRadioModule,
    MatTabsModule,
    MatSelectModule,
    FormsModule,
    MatDialogModule,
    MatDatepickerModule,
    MatCardModule,
    ReactiveFormsModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatGridList,
    MatGridTile,
    MatSlideToggleModule,
    MatMenuModule,
    MatListModule,
    MatChip,
    MatChipSet,
    MatChipGrid,
    MatChipRow,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelTitle,
    MatExpansionPanelHeader
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAnimationsAsync(),
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideNativeDateAdapter()
  ]
})
export class AppModule {}
